import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, CardHeader, Pill, Metric, Button } from "../components/ui";
import { SkeletonCards } from "../components/shared";
import { API_BASE } from "../config";

export default function DataManager() {
  const { data: status, loading: ld1 } = useApi("/status", 5000);
  const { data: stratFiles, loading: ld2 } = useApi("/strategy-files", 30000);
  const { data: telegramStatus } = useApi("/telegram/status", 30000);

  if (ld1 && ld2) return <SkeletonCards n={4} />;

  const strategies = status?.strategies || {};
  const files = stratFiles?.files || [];
  const telegram = telegramStatus || {};

  return (
    <>
      {/* Overview Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { l: "Strategies", v: Object.keys(strategies).length, s: "configured" },
          { l: "Pine Scripts", v: files.length, s: "uploaded" },
          { l: "Telegram", v: telegram.connected ? "Connected" : "—", c: telegram.connected ? theme.greenText : theme.text3, s: telegram.chat_id || "not configured" },
          { l: "IB Gateway", v: Object.values(status?.adapters || {}).some(a => a.connected) ? "Connected" : "Down", c: Object.values(status?.adapters || {}).some(a => a.connected) ? theme.greenText : theme.redText, s: status?.host || "178.104.155.90" },
        ].map((m, i) => (
          <Card key={i} style={{ padding: "20px 24px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: 8 }}>{m.l}</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fonts.mono, color: m.c || theme.text }}>{m.v}</div>
            <div style={{ fontSize: 11, color: theme.text3, marginTop: 6 }}>{m.s}</div>
          </Card>
        ))}
      </div>

      {/* Strategy Files */}
      {files.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <CardHeader title="Strategy Files" sub={`${files.length} Pine Script configurations`} />
          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  {["File", "Instrument", "Timeframe", "Version", "Status"].map((h) => (
                    <th key={h} style={{ padding: "10px 18px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1px", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => (
                  <tr key={i} className="sl-row" style={{ borderBottom: `1px solid ${theme.divider}` }}>
                    <td style={{ padding: "11px 18px", fontSize: 12, fontWeight: 600, fontFamily: fonts.mono }}>{String(f.filename || f.name || f.strategy_name || "—")}</td>
                    <td style={{ padding: "11px 18px", fontSize: 12, fontFamily: fonts.mono }}>{String(f.instrument || "—")}</td>
                    <td style={{ padding: "11px 18px", fontSize: 12, fontFamily: fonts.mono }}>{String(typeof f.timeframes === "object" ? f.timeframes.entry : f.timeframe || "—")}</td>
                    <td style={{ padding: "11px 18px", fontSize: 12, fontFamily: fonts.mono }}>{String(f.version || "—")}</td>
                    <td style={{ padding: "11px 18px" }}>
                      <Pill bg={theme.greenLight} color={theme.greenText}>Active</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Webhook */}
        <Card>
          <CardHeader title="Webhook" sub="TradingView → Trading Bot" />
          <div style={{ padding: "16px 24px" }}>
            {[
              ["Endpoint", `http://${status?.host || "178.104.155.90"}:8080/webhook`],
              ["Method", "POST"],
              ["Auth", "Bearer Token"],
              ["Format", "TradingView JSON Alert"],
              ["Status", "Active"],
            ].map(([k, v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: i < 4 ? `1px solid ${theme.divider}` : "none" }}>
                <span style={{ fontSize: 12, color: theme.text3 }}>{k}</span>
                <span style={{ fontSize: 12, fontWeight: 600, fontFamily: fonts.mono, color: k === "Status" ? theme.greenText : theme.text, wordBreak: "break-all", maxWidth: "60%", textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Instruments */}
        <Card>
          <CardHeader title="Configured Instruments" />
          <div style={{ padding: "16px 24px" }}>
            {Object.entries(strategies).map(([name, s], i) => (
              <div key={name} style={{ padding: 14, borderRadius: 10, background: theme.tint, marginBottom: i < Object.keys(strategies).length - 1 ? 10 : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{s.instrument || name}</span>
                  <Pill bg={s.status === "live" ? theme.greenLight : theme.amberLight} color={s.status === "live" ? theme.greenText : theme.amberText}>
                    {s.status || "stopped"}
                  </Pill>
                </div>
                <div style={{ fontSize: 11, color: theme.text3, marginTop: 4 }}>
                  {s.exchange || "IBKR"} · {s.timeframe || "M5"} · {s.account || "Paper"}
                </div>
                <div style={{ fontSize: 11, color: theme.blueText, fontWeight: 600, marginTop: 2 }}>
                  Session: {s.session || "08:00–16:00 ET"}
                </div>
              </div>
            ))}
            {Object.keys(strategies).length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: theme.text3 }}>No instruments configured</div>
            )}
          </div>
        </Card>
      </div>

      {/* Telegram */}
      {telegram.connected && (
        <Card style={{ marginTop: 16 }}>
          <CardHeader title="Telegram Integration" sub="Notifications & alerts" right={
            <Pill bg={theme.greenLight} color={theme.greenText}>Connected</Pill>
          } />
          <div style={{ padding: "16px 24px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <Metric label="Chat ID" value={telegram.chat_id || "—"} />
            <Metric label="Notifications" value={telegram.notifications_sent || 0} />
            <Metric label="Last Sent" value={telegram.last_sent || "—"} />
          </div>
        </Card>
      )}
    </>
  );
}
